import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

/**
 * v77.31 — Candidate Shortlist context for verified employers.
 *
 * Tracks which Open-for-Work listings the current employer has bookmarked
 * so candidate cards (and the Candidates search page) can render the
 * filled/empty bookmark icon without a per-card API call.
 *
 * Only loads (and only POSTs/DELETEs) when the user is `employer_verified`
 * or admin. Unverified users see an empty set and `toggle` no-ops.
 */
const ShortlistContext = createContext(null);

function isEmployer(user) {
    if (!user) return false;
    if (user.role === "admin") return true;
    return !!user.employer_verified;
}

export function ShortlistProvider({ children }) {
    const { user } = useAuth();
    const [ids, setIds] = useState(new Set());
    const [count, setCount] = useState(0);
    const [ready, setReady] = useState(false);

    const enabled = isEmployer(user);

    const load = useCallback(async () => {
        if (!enabled) {
            setIds(new Set());
            setCount(0);
            setReady(true);
            return;
        }
        try {
            const { data } = await api.get("/employer/shortlist/ids");
            setIds(new Set(data.ids || []));
            setCount(data.count || 0);
        } catch {
            setIds(new Set());
            setCount(0);
        } finally {
            setReady(true);
        }
    }, [enabled]);

    useEffect(() => { setReady(false); load(); }, [load]);

    /** Optimistic toggle. Returns the new saved-state (true=saved). */
    const toggle = useCallback(async (listingId) => {
        if (!enabled) return false;
        const wasSaved = ids.has(listingId);
        // optimistic flip
        setIds((prev) => {
            const next = new Set(prev);
            if (wasSaved) next.delete(listingId); else next.add(listingId);
            return next;
        });
        setCount((c) => c + (wasSaved ? -1 : 1));
        try {
            const { data } = wasSaved
                ? await api.delete(`/employer/shortlist/${listingId}`)
                : await api.post(`/employer/shortlist/${listingId}`);
            if (typeof data?.count === "number") setCount(data.count);
            return !!data?.saved;
        } catch (e) {
            // rollback
            setIds((prev) => {
                const next = new Set(prev);
                if (wasSaved) next.add(listingId); else next.delete(listingId);
                return next;
            });
            setCount((c) => c + (wasSaved ? 1 : -1));
            throw e;
        }
    }, [enabled, ids]);

    const isSaved = useCallback((listingId) => ids.has(listingId), [ids]);

    return (
        <ShortlistContext.Provider value={{ enabled, ids, count, isSaved, toggle, reload: load, ready }}>
            {children}
        </ShortlistContext.Provider>
    );
}

export const useShortlist = () => useContext(ShortlistContext) || {
    enabled: false,
    ids: new Set(),
    count: 0,
    isSaved: () => false,
    toggle: async () => false,
    reload: () => { },
    ready: false,
};
